import { NamedAction } from './api_types';
import * as tesla from './tesla_types'

export const MQTT_STREAM_CHARGER_STATE = 'charger_state'; //keyed by siteId
export const MQTT_STREAM_CONTROL_STATUS = 'control_status'; //keyed by siteId
export const MQTT_STREAM_EVSE_STATE = 'evse_state'; //keyed by ocppId
export const MQTT_STREAM_EVSE_CONTROL = 'evse_control';
export const MQTT_STREAM_EVSE_COMMAND = 'evse_command';
export const MQTT_STREAM_SITE_METERS = 'site_meters';
export const MQTT_STREAM_SITE_CONFIG = 'site_config';
export const MQTT_STREAM_SITE_SECRETS = 'site_secrets';
export const MQTT_STREAM_SITE_SETTINGS = 'site_settings';
export const MQTT_STREAM_OVERRIDE_SETTINGS = 'override_settings';
export const MQTT_STREAM_SITE_STATE = 'site_state';
export const MQTT_STREAM_TESLA_STATE = 'tesla_state';
export const MQTT_STREAM_TESLA_WORKER_COMMAND = 'tesla_worker_command';
export const MQTT_STREAM_NEM_PRICE = 'nem_price';
export const MQTT_STREAM_AMBER_PRICE = 'amber_price'; //keyed by (our) siteId
export const MQTT_STREAM_CONTROLLER_COMMAND = 'controller_command'; //keyed by siteid
export const MQTT_STREAM_SOLAR_WORKER_COMMAND = 'solar_worker_command'; //keyed by siteId

export const ALERT_ID_DISABLE_CONTROL_ENV = 'disable-control-env'
export const ALERT_ID_EVSE_NEVER_CONNECTED = 'evse-never-connected'
export const ALERT_ID_EVSE_NEVER_CONNECTED_TESLA = 'evse-never-connected-tesla'
export const ALERT_ID_EVSE_OFFLINE = 'evse-offline'
export const ALERT_ID_HOME_LOCATION_UNSET = 'home-location-unset'
export const ALERT_ID_START_STOP_PROBLEM_OCPP = 'start-stop-problem-ocpp'
export const ALERT_ID_START_STOP_PROBLEM_TESLA = 'start-stop-problem-tesla'
export const ALERT_ID_TESLA_PAIRING_UNKNOWN = 'tesla-pairing-unknown'
export const ALERT_ID_TESLA_UNPAIRED = 'tesla-unpaired'
export const ALERT_ID_TESLA_VEHICLE_SCOPES = 'tesla-vehicle-scopes'
export const ALERT_ID_TESLA_WAKE_FAILED = 'tesla-wake-failed'
export const ALERT_ID_TESLA_SCHEDULE_MISSING = 'tesla-schedule-missing'
export const ALERT_ID_TESLA_POLL_ERROR = 'tesla-poll-error' //unknown polling error

export const enum ChargeControllerType {
  Ocpp = 'OCPP',
  Tesla = 'Tesla',
  None = 'None',
  Mock = 'Mock', //unit testing only
}

export const enum ChargeMode {
    Off = 'Off', //do not charge
    Solar = 'Solar', //charge at a varying rate, may be zero
    Max = 'Max', //charge at a fixed rate, may be zero
    External = 'External', //external control detected, we will not control charging
    Manual = 'Manual', //manual control only, do not automatically send commands
}

export const enum ControlMode {
  Stop = 'Stop',
  Auto = 'Auto',
  ChargeNow = 'Charge Now',
  External = 'External',
  Unknown = 'Unknown' //should not occur
}

export const enum ActiveFeature {
  NoController = 'NoController', //no control, no control device configured
  ControlDisabled = 'ControlDisabled', //no control, control disabled by setting
  AwayFromHome = 'AwayFromHome', //no control, vehicle away from home OR fast charging OR in service mode
  EvseTestMode = 'EvseTestMode', //smart charger test mode is active, charger will ignore power limits
  ChargeNowMode = 'ChargeNowMode', //charge, due to charge now override
  StopMode = 'StopMode', //stop, due to stop override
  ExternalMode = 'ExternalMode', //no control, external control override
  ScheduleCharge = 'ScheduleCharge', //charge, due to "always charge" schedule
  ScheduleStop = 'ScheduleStop', //stop, due to "do not charge" schedule
  ScheduleChargeLimit = 'ScheduleChargeLimit', //stop, schedule limit reached and no other feature active
  HighPrice = 'HighPrice', //stop, grid price is above Do Not Charge Price
  LowPrice = 'LowPrice', //charge, grid price is below Always Charge Price
  HighRenewables = 'HighRenewables', //charge, grid renewables above Min Renewables, no other feature active
  LowRenewables = 'LowRenewables', //stop, grid renewables below Min Renewables
  SolarDataStopped = 'SolarDataStopped', //stop, solar data has stopped updating
  SolarQuota = 'SolarQuota', //tesla solar command quota reached
  NoGeneration = 'NoGeneration', //stop, no solar generation
  LowGeneration = 'LowGeneration', //stop, generation below min generation limit
  LowExcess = 'LowExcess', //stop, excess solar below min excess limit
  LowSolar = 'LowSolar', //stop, not enough solar available
  LowSolarTimer = 'LowSolarTimer', //charge, low solar timer started
  SolarCharging = 'SolarCharging', //charge, solar tracking
  HoldTimer = 'HoldTimer', //stop, holding, waiting for restart timer to elapse
  DefaultStop = 'DefaultStop', //stop, no feature active ("new" design)
  NoFeature = 'NoFeature', //manual control, no feature enabled
}

export const enum EvseConnectionState {
  NeverConnected = 'Never Connected',
  Online = 'Online',
  Offline = 'Offline'
}

export const enum SimpleChargeState {
  Unplugged = 'Unplugged',
  Charging = 'Charging',
  Starting = 'Starting',
  Stopping = 'Stopping', //tesla only
  Complete = 'Complete', //plugged in, not charging, charged to limit (vehicle only)
  Stopped = 'Stopped', //plugged in (maybe), not charging, stopped by controller (vehicle and EVSE)
  StoppedByEv = 'Stopped By EV', //plugged in, not charging, stopped by EV (EVSE only)
  Faulted = 'Faulted',
  Unknown = 'Unknown',
}

// features which can be enabled based on a user's plan
export interface FeatureSet {
  solarTracking?: boolean
  dataDownload?: boolean
}

export interface LatLong {
  lat: number
  long: number
}

// a custom type representing power flows etc for a site,
// typically from an inverter or monitoring system
// all fields are optional because some systems don't have consumption
// monitoring and some systems don't have solar
export interface SiteMeters {
  import_kw?: number;
  export_kw?: number;
  net_import_kw?: number; //negative means export
  consumption_kw?: number;
  production_kw?: number;
  battery_discharge_kw?: number //negative mean charging
  battery_discharge_kwh?: number //cumalative battery discharge
  battery_charge_kwh?: number //cumalative battery charge
  battery_soc?: number //eg 0.5 = 50%
  battery_energy_kwh?: number //amount of energy in the battery
  import_kwh?: number; //cumulative, not always supplied
  export_kwh?: number; //cumulative, not always supplied
}

export interface SiteMeterState {
  tsms?: number //timestamp that sample was taken (ms)
  lag?: number //the time lag on the feed, if known (seconds)
  type?: EnergyMonitorType
  error?: string //set if there was an error obtaining data
  deviceOffline?: boolean //is the device (eg inverter) offline? (not an error)
  meters?: SiteMeters
}

export interface PowerFlows {
  pvKw?: number //solar generation
  importKw?: number //grid import, negative means export
  batDischargeKw?: number //battery discharge, negative means charge
  batSoc?: number //eg 0.5 = 50%
  houseKw?: number //house load (excludes ev charging), can be negative
  evKw?: number //EV charging power (will be zero when vehicle charging away from home)
  solarTsms?: number //timestamp of solar data
}

export interface PriceState {
  priceCpkwh?: number //import price
  exportPriceCpkwh?: number
  highPriceAlert?: boolean //does the price exceed our max limit?
  lowPriceAlert?: boolean //is the price below our "charge now" setting?
  renewablesPercentage?: number //eg 10 = 10%
  lowRenewablesAlert?: boolean //grid renewables below min renewables
  highRenewablesAlert?: boolean //grid renewables above min renewables
}

export interface ScheduleState {
  enabled: boolean // scheduling is enabled
  chargeMode?: ChargeMode // effective charge mode (not necessarily equal to the period)
  period?: ChargingSchedulePeriod // the active period
  chargeLimitReached?: boolean //vehicle is charged to schedule limit
}

export enum LowSolarReason {
  NoGeneration = 'NoGeneration',    // generation is effectively zero
  LowGeneration = 'LowGeneration',  // generation below min generation setting
  LowExcess = 'LowExcess',          // excess below min excess setting
  LowAvailable = 'LowAvailable',    // available solar is below min charge rate
}

export interface SolarTrackingState {
  chargePriority?: ChargePriority // the effective charge priority (Battery or EV)
  solarTrackingMarginKw?: number  // the solar tracking margin setting value
  excessSolarKw?: number          // the amount of excess solar (ignoring margin)
  targetPowerKw?: number          // the target charging power / available solar (ignoring charger limits)
  solarTargetA?: number           // the target charging amps (constrained by charger limits)
  lowSolarReason?: LowSolarReason // the reason for the low solar state
  lowSolarWarning?: boolean       // low solar, charging, charging will stop after timeout
  solarDataStopped?: boolean      // the site meters are too old to calculate solar
  phases?: number                 // the number of phases used for target amps calc
  chargingFromBattery?: true      // ALPHA. set when in charge-from-battery mode
  teslaCommandReserveReached?: true // the command quota reserve has been reached - stop solar charging
}

export interface ControlStatus {
  controlMode: ControlMode                   // Stop, Auto, Charge Now
  activeFeature: ActiveFeature
  chargingEnabled: boolean | null            // true=charging allowed, false=charging not allowed, null=control disabled
  chargeMode: ChargeMode | 'None'            // None means no control
  chargePriority?: ChargePriority
  targetAmps?: number
}

export enum AlertMessagePriority {
  Info = 'info',
  Error = 'error'
}

export interface AlertMessage {
  priority?: AlertMessagePriority //defaults to info
  title?: string
  message: string
  // added in app 2.4, api version 2, Nov 2023
  minApiVersion?: number //the min app api version required to handle this alert
  appRoute?: string //eg '/config/tesla', only shown if app meets min api version
  learnMoreUrl?: string
  // added in app 2.6, api version 3, Feb 2024
  dropPreApiVersion?: number //drop this alert for clients older than this api version
  moreText?: string
  namedAction?: NamedAction
  actionText?: string //the text for the CTA button
}

export enum ControlStatusStyle {
  Alert = 'alert'
}

export interface ControlStatusItem {
  title?: string //optional, shown in bold above the text
  text: string
  notes?: string[] //optional and can be hidden by the user
  style?: ControlStatusStyle
  learnMoreUrl?: string
  namedAction?: NamedAction //show a CTA button for this action
  actionText?: string //the text for the CTA button
  nextScheduleText?: string //text for upcoming schedule period
}

// (almost) generic vehicle state
export interface VehicleState {
  name?: string
  atHome?: boolean //unset means unknown
  batteryLevel?: number //50 = 50%
  rangeKm?: number
  minutesRemaining?: number //time until charging complete
  teslaSleepState?: TeslaSleepState
}

export interface SiteState {
    activeFeature: ActiveFeature
    chargeMode?: ChargeMode;
    chargeModeOverride?: ChargeMode //deprecated 24/01/23, use override
    override?: OverrideSettings
    chargingEnabled: boolean | null //true=charging allowed, false=charging not allowed, null=control disabled
    chargingStarting?: boolean //attempting to start charging
    chargingStopping?: boolean //attempting to stop charging
    holding?: boolean  //holding the charge mode (off) to avoid contactor wear. DEPRECATED in api version 3
    holdMinutesRemaining?: number //number of minutes left until hold released
    simpleChargeState?: SimpleChargeState
    meterState?: SiteMeterState
    evseState?: EvseState; //state from the wall charger (not the vehicle), if present
    chargerState?: ChargerState //generic state of the evse or the tesla (whichever is in control)
    powerFlows?: PowerFlows
    setPointAmps?: number //not set when not applicable, eg stopped or no control
    scheduleState?: ScheduleState;
    teslaState?: TeslaState;
    priceState?: PriceState
    solarTrackingState?: SolarTrackingState //only set when enabled
    vehicleAtHome?: boolean //is the vehicle (tesla) at home? DEPRECATED by vehicle.atHome
    teslaCommandQuotaResetAt?: number //set when tesla command quota reached (tsms)
    // alert messages are generic messages that the app should display, added v2.0
    alerts?: AlertMessage[]
    // below fields added in API version 4 app 2.6
    controlStatus?: ControlStatusItem
    controlSuspended?: true //actually used to disable the app's control mode selector
    alertIds?: string[] //set by controller
    vehicle?: VehicleState //currently active vehicle
    // DEPRECATED by alerts. Used by app v1.3 to 1.9. Set by the api from alerts.
    infoMessages?: string[]
    // what api version was this object built for?
    // TODO: this is used internally by the api and shouldn't be part of the type
    apiVersion?: number
}

export interface EvseFeatures {
  connectorLock?: boolean // supports keeping the connector locked (socket chargers only)
}

export interface EvseSettings {
  minPowerA: number
  maxPowerA: number
  name?: string
  lockConnector?: boolean //keep the cable locked to the charger
  profileName?: string //selected charger profile, usually unset (automatic)
  // ***** test mode settings *****
  testMode?: boolean //enable testing mode
  rejectConnection?: boolean //reject all connections from charger
}

// matches BootNotificationReq, but we don't want to share that file
export interface EvseAttributes {
  chargeBoxSerialNumber?: string;
  chargePointModel: string;
  chargePointSerialNumber?: string;
  chargePointVendor: string;
  firmwareVersion?: string;
  iccid?: string;
  imsi?: string;
  meterSerialNumber?: string;
  meterType?: string;
}

export interface EvseConfig {
  ocppId: string
  firstSeenAt?: string //timestamp of first connection (ISO format)
  connectionState: EvseConnectionState
  settings: EvseSettings
  features?: EvseFeatures
  attributes?: EvseAttributes
}

export enum EnergyMonitorType {
  AlphaEss = 'AlphaESS',
  Enphase = 'Enphase',
  FroniusSolarweb = 'Fronius Solarweb',
  Goodwe = 'Goodwe',
  Growatt = 'Growatt',
  Powersensor = 'Powersensor',
  Powerwall = 'powerwall',
  PushApi = 'Push API',
  Sim = 'sim',
  SolarAnalytics = 'Solar Analytics',
  SolarEdgeApi = 'solarEdgeApi',
  Solax = 'Solax',
  Solis = 'Solis',
  SungrowISolarCloud = 'Sungrow iSolarCloud',
  Wattwatchers = 'Wattwatchers',
}

export interface AlphaEssCredentials {
  // the new open api requires these fields
  sysSn?: string
  checkCode?: string //printed on inverter label
  verificationCode?: string //emailed to system owner on request
  // the old web api used username and password
  username?: string
  password?: string
}

export interface AlphaEssConfig {
  sysSn?: string //may be specified or discovered
  checkCode?: string //printed on inverter label
  verificationCode?: string //emailed to system owner on request
  // fields for the old web api, no longer used
  username?: string
  password?: string //encrypted except in the app API
  systems?: {
    sysSn: string
    sysName: string
  }[]
}

export interface EnphaseConfig {
  authorizationCode?: string //oauth grant code eg erMhzC, required on create
  systemId: number //may be specified or discovered
  // below fields are discovered
  systems?: {
    systemId: number
    name: string
  }[]
}

// Growatt operates 4 backend systems
export enum GrowattRegion {
  Australia = 'au', //and New Zealand
  China = 'cn',
  Global = 'global',
  US = 'us',
}

export interface GrowattCredentials {
  username: string //growatt username
  // When a user first attempts to connect they provide their username
  // and inverter serial number. We send this to Growatt who then connect
  // the user's account to our API key, then we can access the site.
  // This field is only used to arrange access, not when polling.
  inverterSn: string
  // region added July 2024. unset or blank means automatic selection
  region?: GrowattRegion
}

export interface GrowattDevice {
  deviceType: number //see GrowattDeviceType
  deviceSn: string //device serial number
  deviceTypeDesc?: string //added Feb 2024, may not be set for older sites
  // below fields added for type3 meters in Feb 2024
  dataloggerSn?: string
  address?: string
  // below field added May 2024 for testing battery data from type 7 inverters
  useType7BatteryData?: boolean
}

export interface GrowattConfig {
  username: string //growatt username
  // When a user first attempts to connect they provide their username
  // and inverter serial number. We send this to Growatt who then connect
  // the user's account to our API key, then we can access the site.
  // This field is only used to arrange access, not when polling.
  inverterSn?: string
  // PlantId is set during discovery once we have access to the site.
  // It will be undefined for sites that we don't have access to yet.
  // It can also be changed when the user selects a different site.
  plantId?: number
  // below fields are discovered
  apiTimezone?: string //timezone of selected plant as returned by api eg "GMT+10"
  timezone?: string //timezone in format suitable for luxon eg "UTC+10"
  plants?: {
    plantId: number
    name: string
  }[]
  // details for all devices in selected plant
  devices?: GrowattDevice[]
  // region added July 2024. Defaults to Australia.
  region?: GrowattRegion
  // as of 2024-03-05 we use device data for all sites
  //useDeviceData? boolean
}

export interface PowersensorCredentials {
  email: string
}

export interface PowersensorConfig {
  email: string
}

// these aren't credentials, rather they are settings which control the simulator.
// since we no longer allow updating solar config, they must be specified as credentials
// when creating the "connection"
export interface SimSolarCredentials {
  updateInterval?: number //seconds
  lagSeconds?: number //simulate a non-realtime feed
  hasMeter?: boolean //simulate a consumption meter
  hasBattery?: boolean //simulate a battery
  excludeCharging?: boolean //exclude charging power from consumption
  unmonitoredProduction?: boolean //simulate an unmonitored inverter
}

export type SimSolarConfig = SimSolarCredentials

export interface SolarEdgeCredentials {
  siteId: string
  apiKey: string
}

export interface SolarEdgeApiConfig {
  siteId: string
  apiKey: string
  // below are discovered
  hasMeter: boolean
}

export enum SolarAnalyticsApiType {
  Public = 'Public', //slow
  Live = 'Live' //fast, as used by their webapp, by special agreement
}

export interface SolarAnalyticsCredentials {
  username: string
  password: string
  apiType?: SolarAnalyticsApiType //defaults to Live
}

export interface SolarAnalyticsConfig {
  username: string
  password: string //encrypted except in the app API
  apiType?: SolarAnalyticsApiType //default to Public
  siteId?: number //may be specified or discovered
  timezone?: string //discovered
  siteName?: string //discovered
  // added Jan 2023, may not be set
  sites?: {
    siteId: number
    name: string
  }[]
}

export interface SolaxCredentials {
  tokenId: string //obtained from the portal
  sn1: string //SN of comms device, not the inverter itself
  sn2?: string
  sn3?: string
  timezone: string //eg 'Australia/Sydney', needed to parse timestamps
}

export interface SolaxDevice {
  inverterSn: string
  sn: string //"RegistrationSN" in the portal, this is required to access the API
  inverterType: string
}

export interface SolaxConfig {
  timezone: string //eg 'Australia/Sydney'
  devices: SolaxDevice[]
}

export interface SolisCredentials {
  plantSno: string //eg 1234B2
}

export interface SolisConfig {
  plantSno: string
  plantId: string
}

// our type to identify the sungrow regional servers
export enum SungrowServer {
  AU = 'au', //Australian server
  EU = 'eu', //European server
  HK = 'hk', //International server
  // we don't use the Chinese server currently
}

export interface SungrowCredentials {
  // either psId or commsSn must be specified
  psId?: string //used by older apps
  // commsSn added Jun 2024 and is the method used by newer apps
  commsSn?: string //sn of any comms device at the plant
  // server added Jun 2024, defaults to automatic if not set
  server?: SungrowServer
}

export interface SungrowDevice {
  deviceType: number, //1=solar inverter, 7=smart meter, 14=hybrid inverter, 22=WiFi module
  psKey: string //eg 1130953_14_1_1
  model: string //eg SG5K-D
  name: string //eg SG5K-D_001_001
  typeName: string //eg Inverter
  // below added 28/11/22 and won't be set for existing sites
  sn?: string //eg B21A1201212
  // below added August 2023 and won't be set for existing sites
  devStatus?: string //'0' = Offline, '1' = Online
}

export interface SungrowConfig {
  psId: string
  // below fields are discovered, added in April 2022
  devices?: SungrowDevice[]
  // server added May 2024. Defaults to AU if not set
  server?: SungrowServer
  // below commented fields haven't been used for a while
  //pvInverterPsKeys?: string[] // regular inverters (type 1), not hybrid
  //meterPsKey?: string // used with regular inverters
  //hybridInverterPsKey?: string // (type 14)
}

export interface FroniusCredentials {
  pvSystemId: string
}

export interface FroniusConfig {
  pvSystemId: string
  // below fields are discovered
  name?: string
}

export const enum GoodweDiscoveryMethod {
  Sems = 'sems',
  SerialNos = 'sns',
}

export interface GoodweCredentials {
  // user can specify their SEMS credentials which are used to discover their site,
  // or they can specify their serial numbers instead
  method?: GoodweDiscoveryMethod
  // sems fields
  username?: string
  password?: string
  // sns fields
  inverterSn1?: string
  inverterSn2?: string
  homekitSn?: string
}

export interface GoodweConfig {
  method?: GoodweDiscoveryMethod
  username?: string
  password?: string //encrypted except in the API
  powerStationId?: string // may be specified or set automatically
  sns?: string[] //inverter serial numbers (for selected powerstation)
  homekitSn?: string //homekit serial number (for selected powerstation)
  powerStations?: { //all powerstations
    powerStationId: string
    name: string
    inverterSns: string[]
    homekitSn?: string
  }[]
}

export interface PowerwallCredentials {
  // credentials are actually stored in the tesla account record, but the app allows
  // selecting an energy site when first creating the connection.
  // NOTE: this field is not updated when selecting a different energy site,
  // and thus may not match the energySiteId in the powerwall config
  energySiteId?: string
}

export interface PowerwallConfig {
  energySiteId: string
  energySiteName: string
  hasBattery: boolean
  hasLoadMeter: boolean
  // added Jan 2023 - may not be set
  energySites?: {
    name: string
    energySiteId: string
    hasBattery: boolean
    hasLoadMeter: boolean
  }[]
}

export interface WattwatchersCredentials {
  deviceId: string //selected device
}

// this object is derived from the device info returned by the API
export interface WattwatchersDeviceConfig {
  id: string //device ID
  channels: {
    id: string //"DD04108376106_C1",
    label: string //"Grid",
    categoryId: number //1,
    categoryLabel: string //"Grid connect",
    ctRating: number //60
  }[],
  // these fields group the channels by category that we care about
  // the values are channel indexes (zero based)
  gridChannels: number[] //grid connections
  solarChannels: number[] //solar generation
  hybridChannels: number[] //hybrid inverter
  batteryChannels: number[] //AC-coupled batteries
}

export interface WattwatchersConfig {
  deviceId: string //selected device
  // below are discovered
  deviceConfig?: WattwatchersDeviceConfig
}

// contains credentials for connecting to a solar data source
// the substructs have the same names as the EnergyMonitorType values for convenience
export interface SolarCredentials {
  type: EnergyMonitorType
  AlphaESS?: AlphaEssCredentials
  'Fronius Solarweb'?: FroniusCredentials
  Goodwe?: GoodweCredentials
  Growatt?: GrowattCredentials
  Powersensor?: PowersensorCredentials
  powerwall?: PowerwallCredentials
  solarEdgeApi?: SolarEdgeCredentials
  'Solar Analytics'?: SolarAnalyticsCredentials
  Solax?: SolaxCredentials
  Solis?: SolisCredentials
  sim?: SimSolarCredentials
  'Sungrow iSolarCloud'?: SungrowCredentials
  Wattwatchers?: WattwatchersCredentials
}

// the substructs have the same names as the EnergyMonitorType values for convenience
export interface EnergyMonitorConfig {
  type: EnergyMonitorType
  AlphaESS?: AlphaEssConfig
  Enphase?: EnphaseConfig
  'Fronius Solarweb'?: FroniusConfig
  Goodwe?: GoodweConfig
  Growatt?: GrowattConfig
  Powersensor?: PowersensorConfig
  powerwall?: PowerwallConfig
  solarEdgeApi?: SolarEdgeApiConfig
  'Solar Analytics'?: SolarAnalyticsConfig
  Solax?: SolaxConfig
  Solis?: SolisConfig
  sim?: SimSolarConfig
  'Sungrow iSolarCloud'?: SungrowConfig
  Wattwatchers?: WattwatchersConfig

  // is this object encrypted? (if we know)
  encrypted?: boolean

  // a connection may be in a "pending activation" state, which means we are waiting
  // for a third party to enable something before we can connect, eg Growatt
  pendingActivation?: true
  pendingSince?: string //ISO date
}

// site secrets are stored encrypted in the database
export interface SiteSecrets {
  solarCredentials?: SolarCredentials,
  // enphase is not currently used
  enphase?: {
    refreshToken: string
  }
}

// a flag set for the tesla access token scopes
export interface TeslaScopeSet {
  vehicleData: boolean
  vehicleCommands: boolean
  vehicleChargingCommands: boolean
  energyData: boolean
  energyCommands: boolean
}

export interface TeslaConfig {
  teslaId: string;  //our internal UUID. TODO remove this?
  fleetApi: boolean //is this account using the fleetapi?
  region: tesla.TeslaRegion | null //tesla account region. May be unset for older accounts
  name: string | null //vehicle name (null means no vehicles)
  id_s: string | null  //vehicle id in Tesla API (null means no vehicles)
  apiTier: tesla.TeslaApiTier | null //vehicle API tier
  api_version: number | null //vehicle api version (null means no vehicles)
  commandReadiness?: tesla.TeslaCommandReadiness //is the vehicle able to accept commands?
  vehicleSettings: tesla.TeslaVehicleSettings | null //settings etc on the vehicle
  hasEnergySite?: boolean //tesla account has an energy site (solar / powerwall)
  hasPowerwall?: boolean //tesla account has a powerwall
  tokenInvalid?: true //token is invalid and will never work
  scopesInvalid?: true //token scopes do not meet minimum requirements
  error?: string //account error message
  invalid?: true //connection is invalid (will never work) for any reason
}

export const enum PriceSource {
  Aemo = 'AEMO',
  Amber = 'Amber', //v1, uses api token
}

// generic representation of the sites available via a solar connection,
// and which one is currently selected
export interface EnergySitesConfig {
  selectedSiteId?: string
  sites: {
    id: string
    name: string
  }[]
}

// discovered solar attributes
export interface SolarAttributes {
  hasSolar: boolean
  hasMeter: boolean
  hasBattery: boolean
}

// SiteConfig describes the setup of a site
export interface SiteConfig {
  createdAt: string //ISO date/time, site creation
  apiKey?: string
  country?: string //country code eg 'AU'
  timezone?: string //eg 'Australia/Sydney'
  chargeControllerType: ChargeControllerType
  evses: EvseConfig[];
  energyMonitor?: EnergyMonitorConfig;
  energySites?: EnergySitesConfig //only set for types which support site selection
  tesla?: TeslaConfig;
  hasSoc: boolean //do we know the vehicle SoC?
  hasPowerControl: boolean
  minValidChargeAmps?: number //the min amps that can be used for charging
  maxValidChargeAmps?: number //the max amps that can be used for charging
  // feature set based on user's plan
  featureSet?: FeatureSet
}

export interface ChargingSchedulePeriod {
  // the ID and mpm (minutes-past-midnight) fields were introduced with app 2.2
  id?: string //UUID, although not in unit tests
  // app 2.2 and above will only provide the mpm fields
  // apps prior to 2.2 only provide the startTime/endTime
  // all fields should be set before storing to the database
  startMpm?: number //minutes past midnight. Always less than 1440 (24:00)
  endMpm?: number //minutes past midnight, may go beyond midnight ie the next day. Always greater than zero.
  startTime?: string //eg '08:00' (inclusive), midnight = '00:00'
  endTime?: string //eg '10:00' (exclusive), midnight = '24:00'. Always <= '24:00'
  weekdays?: number[] //days of week. 1 = monday, 7 = sunday
    // undefined means every day (legacy, should not be used)
    // empty means no days
  chargeMode: ChargeMode //Max for charge, Off for blockout
  chargeLimit?: number //eg 40 (%), undefined means no limit
  chargeAmps?: number //undefined means max amps
}

export interface ChargingSchedule {
  enabled: boolean
  periods: ChargingSchedulePeriod[]
}

export enum SolarTrackingMode {
  SolarPlusGrid = 'SolarPlusGrid',
  SolarOnly = 'SolarOnly',
}

export enum SolarTrackingReference {
  ExcessSolar = 'Excess Solar',
  SolarGeneration = 'Solar Generation',
}

export enum ChargePriority {
  EV = 'EV',
  Battery = 'Battery',
}

export interface SiteControl {
    controlEnabled?: boolean                  // enable charging control (default true)
    schedule?: ChargingSchedule
    latLong?: LatLong | null                  // site location
    nemRegion?: string | null                 // "" means None
    priceSource?: PriceSource | null          // the source of the electricity price, "" means None
    amberApiToken?: string | null             // for Amber price source
    amberSiteId?: string | null               // site ID in the Amber API
    solarTracking?: boolean | null            // charge power tracks excess solar
    minSolarKw?: number | null                // stop charging if solar production drops below this value
    minExcessSolarKw?: number | null          // stop charging if excess solar drops below this value
    solarTrackingMode?: SolarTrackingMode | null
    solarTrackingReference?: SolarTrackingReference | null //track excess or generation?
    solarTrackingMarginKw?: number | null     // charge power = [excess] solar - margin
    chargePriority?: ChargePriority | null    // which should be charged first?
    batteryPriorityChargeLimit?: number | null // eg 50 = 50%, when in battery priority
    evPriorityChargeLimit?: number | null // eg 50 = 50%, when in ev priority
    chargeFromBattery?: boolean | null        // charge the EV from the battery
    consumptionExcludesCharging?: boolean | null // does consumption meter exclude charging?
    lowPriceCpkwh?: number | null             // start charging when price <= this value
    highPriceCutoffCpkwh?: number | null      // stop charging if price exeeds this value
    minRenewablesPercentage?: number | null   // stop charging if grid renewables is below this value
    hasThreePhase?: boolean | null            // does the site have 3 phase charging?
    hasNegativeConsumption?: boolean | null   // does the site have negative consumption?
    totalInverterPowerKw?: number | null      // total AC rating of all inverters including battery
    defaultChargeAmps?: number | null         // the charge amps to set when plugged in etc
    maxChargeAmps?: number | null             // the max charge amps allowed everywhere
    overrideExternalControl?: boolean | null  // ignore external start/stop and retain control
    overrideVehicleSchedule?: boolean | null  // ignore charging caused by vehicle schedule and retain control
    constantSolarPolling?: boolean            // solar worker should poll constantly (admin setting)
    lastOcppId?: string                       // the last used ocpp ID, so we can use it again
    // This flag is set for users where solarTrackingMode was unset when the default
    // was changed to solar-only in March 2024. It has no function other than as a record.
    solarTrackingModeWasUnset?: true

}

export enum OverrideReason {
  Manual = 'Manual', // set by the user
  VehicleScheduledCharging = 'VehicleScheduledCharging', // vehicle scheduled charging detected
  ExternalStart = 'ExternalStart', //we detected that someone else started the charging
  ExternalStop = 'ExternalStop', //we detected that someone else stopped the charging
}

// override settings for the current charging session
export interface OverrideSettings {
  chargeMode?: ChargeMode // undefined means no override (ie Auto mode)
  tsms?: number //timestamp (millis) when override was created
  reason?: OverrideReason
}

export interface ControllerCommand {
  setPointAmps?: number
}

// for controlling a OCPP EVSE
export interface EvseControl {
    power_limit_a: number | null //null means no limit
}

export enum EvseCommandType {
  ClearPowerLimit = 'ClearPowerLimit',
  GetDiagnostics = 'GetDiagnostics',
  HardReset = 'HardReset',
  RemoteStart = 'RemoteStart',
  RemoteStop = 'RemoteStop',
  SetPowerLimit = 'SetPowerLimit',
  SoftReset = 'SoftReset',
  UnlockConnector = 'UnlockConnector',
  UpdateFirmware = 'UpdateFirmware',
}

// mirrors the OCPP type
export enum ChargingProfilePurpose {
  ChargePointMaxProfile = 'ChargePointMaxProfile',
  TxDefaultProfile = 'TxDefaultProfile', //default for future transactions
  TxProfile = 'TxProfile', //for the current transaction
}

export interface EvseCommand {
  type: EvseCommandType
  transactionId?: number //required for remote stop and SetPowerLimit
  powerLimitUnit?: 'A' | 'W' //added 18/09/23, default A
  powerLimit?: number //added 18/09/23, power limit in either A or W
  powerLimitA?: number //DEPRECATED 18/09/23, used by older clients
  chargingProfileId?: number //optional for SetPowerLimit
  chargingProfilePurpose?: ChargingProfilePurpose
  updateFirmwareUri?: string //required for firmware update
  locationUri?: string //for get diagnostics
  startTime?: string //for get diagnostics
  stopTime?: string //for get diagnostics
}

export interface EvseMeters {
    tsms: number //timestamp of meters as provided by the charger
    importMeterWh?: number //may or may not reset to zero each session
    power_kw?: number; //total charging power
    voltage?: number; //from first phase
    import_current?: number; //charging amps per phase
    currentOffered?: number; //amps offered
    numPhases?: number //1 or 3, if known
}

// ocpp smart charger state
export interface OcppState {
  plugged_in?: boolean //sometimes we aren't certain
  status?: string //values from OCPP ChargePointStatus
  status_info?: string //status notification info field (optional)
  transaction_id?: number //OCPP transaction ID
  meters?: EvseMeters
  meterStartWh?: number //the import meter value at the start of the current transaction
  firmwareStatus?: string //values from OCPP FirmwareStatus enum
}

// ocpp state plus some internal fields
export interface EvseState extends OcppState {
  online: boolean
  lastSeenAtMs?: number //time we last received data from the charger
}

// generic charger state (ie smart charger or tesla)
export interface ChargerState {
  online: boolean
  lastSeenAtMs?: number //time we last received data from the charger
  pluggedIn?: boolean
  chargeState: SimpleChargeState
  powerKw?: number
  current?: number
  currentOffered?: number
  voltage?: number
  phases?: number
  importedKwh?: number //for the current session
  setPointAmps?: number
}

export interface StatsCounters {
  chargedKwh: number
  fromSolarKwh: number
  fromBatteryKwh: number
  fromGridKwh: number
  fromGridGreenKwh: number
  fromOtherKwh: number
}

export interface SiteCounters {
  updated: number //milliseconds since epoch
  flags: number //flags for the current interval
  evseRatedKw?: number //max rated kw of evse
  interval: StatsCounters
  session: StatsCounters
  lifetime: StatsCounters
}

// our type which represents the state of the vehicle in terms
// of sleep management
export enum TeslaSleepState {
  Unknown = 'Unknown',
  Driving = 'Driving',
  Charging = 'Charging',
  Parked = 'Parked',                       // online, not driving, not charging
  AttemptingSleep = 'Attempting Sleep',    // parked and attempting sleep
  Asleep = 'Asleep',
  Offline = 'Offline',
  InService = 'In Service',                // service mode, eg repair shop
  Shutdown = 'Shutdown',                   // not sure, but polling is not possible
  Removed = 'Removed',                     // vehicle no longer associated with account
  PollQuotaReached = 'Poll Quota Reached',
}

export enum TeslaWorkerCommand {
  Poll = 'Poll', //poll, but don't disrupt sleep or attempting sleep
  ResumePolling = 'ResumePolling', //cancel sleep attempt and poll, but don't wake vehicle
}

export interface TeslaWorkerCommandReq {
  command: TeslaWorkerCommand
  bypassThrottle?: boolean // set to bypass the throttle, ie you expect something to have changed
}

export interface TeslaState {
  name?: string; //can be blank, unset if there are no vehicles
  apiError?: boolean; //API returned an error (but not invalid token)
  connection_state: tesla.ConnectionState; //offline, online, asleep
  is_charging?: boolean;
  sleep_state?: TeslaSleepState;
  idle_time_mins?: number; //time the vehicle has been idling (used for sleep management)
  atHome?: boolean | null //null if unknown eg home location not set
  fields?: tesla.TeslaFields //either polled or cached
  lastSeenAtMs?: number //last time we polled the vehicle successfully
  pollQuotaResetAt?: number //tsms when poll quota will reset, set when limit reached
}

// 5m or 30m interval data as published by AEMO, republished by aemo worker
export interface NemInterval {
  settlement_date: string;
  region: string;
  period_type: string; //ACTUAL or FORECAST (FORECAST for 30m only)
  price: number;
  total_demand: number;
  net_interchange: number;
  scheduled_generation: number;
  semischeduled_generation: number;
}

// This is an internal type published by the aemo worker on MQTT
export interface NemPrice {
  region: string;
  price_estimate_cpkwh: number; //our estimate of the current price
  dispatch_price_cpkwh: number; //the current 5m price
  renewablePercentage: number //including rooftop PV (eg 10 = 10%)
}

// the type published by the amber worker on MQTT
export interface AmberPrice {
  renewablesPercentage: number, // eg 20 = 20%
  spotPriceCpkwh: number,
  priceCpkwh: number, //import price
  feedInCpkwh?: number //positive means customer gets paid
}

export enum SolarWorkerCommand {
  Poll = 'Poll'
}

export interface SolarWorkerCommandReq {
  command: SolarWorkerCommand
}

export const DEFAULT_CHARGE_PRIORITY = ChargePriority.Battery
export const DEFAULT_BATTERY_PRIORITY_CHARGE_LIMIT = 100
export const DEFAULT_EV_PRIORITY_CHARGE_LIMIT = 100
export const DEFAULT_SOLAR_TRACKING_MODE = SolarTrackingMode.SolarOnly
export const DEFAULT_SOLAR_TRACKING_REFERENCE = SolarTrackingReference.ExcessSolar

export enum SocUnit {
  Percent = 'percent',
  Range = 'range'
}

// when changing this type don't forget to update the api validators
export interface AppSettings {
  hidePlanNotices?: boolean
  showControlStatusDetail?: boolean
  socUnit?: SocUnit //display the battery level in percent or range
  // tooltips which have been dismissed and should not be shown
  hideIconShortcutTooltip?: boolean
  hideRefreshTimerTooltip?: boolean
  hideSocToggleTooltip?: boolean
  hideStatusDetailTooltip?: boolean
}

// subset of stripe price interval values that we use
export type PriceInterval = 'month' | 'year'

// the price of a subscription, including free plans
export interface SubscriptionPrice {
  currency?: string //lower case
  interval: PriceInterval
  amountCents: number
  amountDisplay: string //eg '$7.00'
}
